import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { 
  Card, 
  Button, 
  Result, 
  Descriptions, 
  Tag, 
  Space, 
  Divider,
  Alert,
  Timeline
} from 'antd';
import {
  CheckCircleOutlined,
  DollarOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  HomeOutlined,
  ArrowLeftOutlined,
  PhoneOutlined,
  MailOutlined
} from '@ant-design/icons';
import Logo from '../components/general/Logo';
import { getApiUrl } from '../lib/api/config';

const VendorBriefingSuccess = () => {
  const { vendorId, jobId } = useParams();
  const navigate = useNavigate();
  const [bidData, setBidData] = useState(null);
  const [loading, setLoading] = useState(true);
  const apiUrl = getApiUrl();

  useEffect(() => {
    fetchBidStatus();
  }, [vendorId, jobId]);

  const fetchBidStatus = async () => {
    try {
      const response = await fetch(`${apiUrl}/vendor-portal/${vendorId}/${jobId}/bid-status`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setBidData(data.bid);
      }
    } catch (err) {
      console.error('Failed to fetch bid status:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => `$${amount?.toLocaleString() || 0}`;
  
  const getStatusColor = (status) => {
    switch (status) {
      case 'accepted': return 'success';
      case 'rejected': return 'error';
      case 'pending': return 'processing';
      default: return 'default';
    }
  };

  const nextSteps = [
    {
      title: 'Bid Submitted',
      description: 'Your bid has been successfully submitted to the homeowner.',
      status: 'finish'
    },
    {
      title: 'Under Review',
      description: 'The homeowner is reviewing all submitted bids.',
      status: bidData?.status === 'pending' ? 'process' : 'wait'
    },
    {
      title: 'Decision',
      description: bidData?.status === 'accepted' ? 'Congratulations! Your bid was selected.' :
                  bidData?.status === 'rejected' ? 'Your bid was not selected this time.' :
                  'You will be notified when a decision is made.',
      status: bidData?.status === 'accepted' ? 'finish' : 
              bidData?.status === 'rejected' ? 'error' : 'wait'
    }
  ];

  return (
    <SuccessContainer>
      <Header>
        <HeaderLeft>
          <Logo />
          <SystemTitle>VENDOR BRIEFING PORTAL</SystemTitle>
        </HeaderLeft>
        <HeaderRight>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate(`/vendor-portal/${vendorId}/${jobId}`)}
            type="link"
            style={{ color: '#e2e8f0' }}
          >
            Back to Job Details
          </Button>
        </HeaderRight>
      </Header>

      <MainContent>
        <SuccessCard>
          <Result
            status="success"
            title="Bid Submitted Successfully"
            subTitle={`Job ID: ${jobId?.slice(-8)} • Vendor ID: ${vendorId?.slice(-8)}`}
            extra={
              <Space direction="vertical" size="large" style={{ width: '100%' }}>
                {bidData && (
                  <BidSummary>
                    <SummaryTitle>Bid Summary</SummaryTitle>
                    <Descriptions
                      bordered
                      size="small"
                      column={1}
                      labelStyle={{ 
                        background: 'rgba(15, 23, 42, 0.7)', 
                        color: '#94a3b8',
                        fontFamily: 'IBM Plex Mono, monospace',
                        fontSize: '12px'
                      }}
                      contentStyle={{ 
                        background: 'rgba(30, 41, 59, 0.5)', 
                        color: '#e2e8f0',
                        fontWeight: '500'
                      }}
                    >
                      <Descriptions.Item label="Bid Amount">
                        <PriceDisplay>{formatCurrency(bidData.bidAmount)}</PriceDisplay>
                      </Descriptions.Item>
                      <Descriptions.Item label="Estimated Start">
                        <DateDisplay>
                          <CalendarOutlined /> {bidData.estimatedStart ? 
                            new Date(bidData.estimatedStart).toLocaleDateString() : 
                            'TBD'
                          }
                        </DateDisplay>
                      </Descriptions.Item>
                      <Descriptions.Item label="Duration">
                        <DurationDisplay>
                          <ClockCircleOutlined /> {bidData.timeline || 'TBD'}
                        </DurationDisplay>
                      </Descriptions.Item>
                      <Descriptions.Item label="Status">
                        <Tag color={getStatusColor(bidData.status)} icon={<CheckCircleOutlined />}>
                          {bidData.status?.toUpperCase() || 'SUBMITTED'}
                        </Tag>
                      </Descriptions.Item>
                      {bidData.notes && (
                        <Descriptions.Item label="Notes">
                          <NotesText>{bidData.notes}</NotesText>
                        </Descriptions.Item>
                      )}
                    </Descriptions>
                  </BidSummary>
                )}

                <TimelineSection>
                  <TimelineTitle>Next Steps</TimelineTitle>
                  <Timeline items={nextSteps} />
                </TimelineSection>

                <NotificationSection>
                  <Alert
                    type="info"
                    message="Stay Connected"
                    description={
                      <div>
                        <div style={{ marginBottom: '8px' }}>
                          You will receive updates via:
                        </div>
                        <NotificationList>
                          <li><MailOutlined /> Email notifications</li>
                          <li><PhoneOutlined /> SMS alerts (if provided)</li>
                          <li>Dashboard updates in your vendor portal</li>
                        </NotificationList>
                      </div>
                    }
                    showIcon
                  />
                </NotificationSection>

                <ActionButtons>
                  <Button
                    type="primary"
                    size="large"
                    onClick={() => navigate(`/vendor-portal/${vendorId}/${jobId}`)}
                    icon={<ArrowLeftOutlined />}
                  >
                    Return to Job Details
                  </Button>
                  
                  {bidData?.status === 'pending' && (
                    <Button
                      size="large"
                      onClick={() => navigate(`/vendor-portal/${vendorId}/${jobId}?edit=true`)}
                    >
                      Edit Bid
                    </Button>
                  )}
                </ActionButtons>
              </Space>
            }
          />
        </SuccessCard>

        <ContactCard>
          <ContactTitle>Need Assistance?</ContactTitle>
          <ContactInfo>
            <ContactItem>
              <ContactLabel>
                <PhoneOutlined /> Support Phone
              </ContactLabel>
              <ContactValue>(555) 123-CLUMSE</ContactValue>
            </ContactItem>
            <ContactItem>
              <ContactLabel>
                <MailOutlined /> Support Email
              </ContactLabel>
              <ContactValue>vendors@clumse.com</ContactValue>
            </ContactItem>
            <ContactItem>
              <ContactLabel>
                <HomeOutlined /> Business Hours
              </ContactLabel>
              <ContactValue>Mon-Fri 8AM-6PM CST</ContactValue>
            </ContactItem>
          </ContactInfo>
        </ContactCard>
      </MainContent>
    </SuccessContainer>
  );
};

// Styled Components
const SuccessContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(180deg, #0a0f1c 0%, #1a1f2e 100%);
  color: #e2e8f0;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
`;

const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  background: linear-gradient(90deg, #1e293b 0%, #334155 100%);
  border-bottom: 1px solid #475569;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
`;

const SystemTitle = styled.h1`
  font-family: 'IBM Plex Mono', 'SF Mono', Consolas, monospace;
  font-size: 18px;
  font-weight: 700;
  color: #ffffff;
  margin: 0;
  letter-spacing: 1px;
`;

const MainContent = styled.main`
  max-width: 800px;
  margin: 40px auto;
  padding: 0 24px;
`;

const SuccessCard = styled(Card)`
  background: linear-gradient(145deg, #1e293b 0%, #334155 100%);
  border: 1px solid #475569;
  border-radius: 12px;
  margin-bottom: 24px;
  
  .ant-card-body {
    padding: 40px;
  }
  
  .ant-result-title {
    color: #e2e8f0 !important;
    font-weight: 700;
  }
  
  .ant-result-subtitle {
    color: #94a3b8 !important;
    font-family: 'IBM Plex Mono', monospace;
  }
`;

const BidSummary = styled.div`
  width: 100%;
  margin: 24px 0;
`;

const SummaryTitle = styled.h3`
  font-family: 'IBM Plex Mono', monospace;
  font-size: 16px;
  font-weight: 600;
  color: #e2e8f0;
  margin-bottom: 16px;
  text-align: center;
`;

const PriceDisplay = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #10b981;
`;

const DateDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: #e2e8f0;
`;

const DurationDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: #e2e8f0;
`;

const NotesText = styled.div`
  color: #94a3b8;
  font-style: italic;
  line-height: 1.5;
`;

const TimelineSection = styled.div`
  width: 100%;
  margin: 24px 0;
`;

const TimelineTitle = styled.h3`
  font-family: 'IBM Plex Mono', monospace;
  font-size: 16px;
  font-weight: 600;
  color: #e2e8f0;
  margin-bottom: 16px;
  text-align: center;
`;

const NotificationSection = styled.div`
  width: 100%;
  margin: 24px 0;
`;

const NotificationList = styled.ul`
  margin: 0;
  padding-left: 16px;
  
  li {
    margin: 4px 0;
    color: #64748b;
    display: flex;
    align-items: center;
    gap: 8px;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 16px;
  justify-content: center;
  margin-top: 32px;
  
  .ant-btn-primary {
    background: linear-gradient(90deg, #3b82f6 0%, #1d4ed8 100%);
    border: none;
    
    &:hover {
      background: linear-gradient(90deg, #2563eb 0%, #1e40af 100%);
    }
  }
  
  .ant-btn {
    height: 48px;
    padding: 0 24px;
    font-weight: 600;
  }
`;

const ContactCard = styled(Card)`
  background: linear-gradient(145deg, #0f172a 0%, #1e293b 100%);
  border: 1px solid #475569;
  border-radius: 8px;
  
  .ant-card-body {
    padding: 24px;
  }
`;

const ContactTitle = styled.h3`
  font-family: 'IBM Plex Mono', monospace;
  font-size: 16px;
  font-weight: 600;
  color: #e2e8f0;
  margin-bottom: 16px;
  text-align: center;
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ContactItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  background: rgba(15, 23, 42, 0.5);
  border: 1px solid #334155;
  border-radius: 6px;
`;

const ContactLabel = styled.div`
  font-size: 13px;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ContactValue = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #e2e8f0;
  font-family: 'IBM Plex Mono', monospace;
`;

export default VendorBriefingSuccess;